import React, { FC } from 'react'
import { UserOutlined, ClockCircleOutlined, RiseOutlined, SlidersOutlined, ControlOutlined } from '@ant-design/icons'
import { ChoiceSelect, ChoiceSelectOption } from '@pushly/aqe/lib/components/choice-select/choice-select'
import { CUSTOM_PRESET } from './sticky-preset'

export type StrategyPreset = 'personalized' | 'recent' | 'trending' | 'balanced'

/**
 * A reflected, non-applicable selection: it represents an off-preset weight
 * combination. Unlike the named presets, Custom has no weights of its own, so
 * selecting it applies nothing — it only becomes the active value when the
 * ranking weights don't match any named preset. Re-exported from the shared
 * sticky-preset module (see its docstring) under this domain's own name for
 * readability at call sites.
 */
export const CUSTOM_STRATEGY = CUSTOM_PRESET

export type StrategySelection = StrategyPreset | typeof CUSTOM_STRATEGY

export type RankingWeights = {
    personalized: number // 0-4
    recent: number // 0-4
}

type StrategyPresetsProps = {
    active: StrategySelection | null
    onChange: (selection: StrategySelection) => void
    readonly?: boolean
}

export const STRATEGY_PRESETS: Record<
    StrategyPreset,
    { label: string; icon: React.ReactNode; weights: RankingWeights }
> = {
    personalized: {
        label: 'Personalized',
        icon: <UserOutlined aria-hidden="true" />,
        weights: { personalized: 4, recent: 1 },
    },
    recent: {
        label: 'Recent',
        icon: <ClockCircleOutlined aria-hidden="true" />,
        weights: { personalized: 1, recent: 4 },
    },
    trending: {
        label: 'Trending',
        icon: <RiseOutlined aria-hidden="true" />,
        weights: { personalized: 1, recent: 2 },
    },
    balanced: {
        label: 'Balanced',
        icon: <SlidersOutlined aria-hidden="true" />,
        weights: { personalized: 2, recent: 2 },
    },
}

const STRATEGY_OPTIONS: ChoiceSelectOption<StrategySelection>[] = [
    ...Object.entries(STRATEGY_PRESETS).map(([key, preset]) => ({
        value: key as StrategySelection,
        label: preset.label,
        icon: preset.icon,
    })),
    {
        value: CUSTOM_STRATEGY,
        label: 'Custom',
        icon: <ControlOutlined aria-hidden="true" />,
    },
]

/**
 * Strategy preset selector using AQE's ChoiceSelect (button variant).
 *
 * No local ConfigProvider override or custom SCSS needed — ChoiceSelect
 * handles the detached-pill structural styling and inherits brand color
 * from the ambient theme's colorPrimary automatically.
 *
 * Selecting a named preset applies that preset's weights. Selecting "Custom"
 * retains the current weights and switches the selector into hand-tuning mode
 * (mirroring how "Custom" works in a date-range picker) — the caller owns that
 * distinction, so this component just forwards the raw selection.
 */
export const VespaStrategyPresets: FC<StrategyPresetsProps> = ({ active, onChange, readonly }) => {
    return (
        <ChoiceSelect<StrategySelection>
            options={STRATEGY_OPTIONS}
            value={active ?? undefined}
            onChange={onChange}
            disabled={readonly}
        />
    )
}
